<template>
  <SectionWithAnchor :class="$style.container" :anchorId="AnchorLinks.LocationCarousel">
    <h2 :class="$style.heading">{{ $t('landing.locationCarousel.title') }}</h2>
    <HgSpinner v-if="isResolvingLocations" :class="$style.spinner" />
    <HgSlider
      v-else
      :breakpoints="sliderBreakpoints"
      :activeSlideIndex="0"
      :visibleSlides="4"
      :gap="20"
      sliderKey="title"
      type="slide"
      :sliderComponents="locationsData"
    >
      <template #default="sliderComponent">
        <HgCardElevated
          :class="$style.cardWrapper"
          :to="sliderComponent.href"
          @click="handleLinkClick(sliderComponent.geoLocation)"
        >
          <div @click="$event.stopPropagation()">
            <img
              :class="$style.image"
              loading="lazy"
              :alt="sliderComponent.title"
              :src="images[sliderComponent.geoLocation.id]"
            />
            <div :class="$style.contentWrapper">
              <h4 :class="$style.cardTitle">{{ sliderComponent.title }}</h4>
              <p :class="$style.cardPrice">
                <HgIcon icon="House" :themeClass="$style.icon" />
                {{ sliderComponent.text.priceForHouse }}
                CHF/<span>m<sup>2</sup></span>
              </p>
              <p :class="$style.cardPrice">
                <HgIcon icon="Apartment" :themeClass="$style.icon" />
                {{ sliderComponent.text.priceForApartment }}
                CHF/<span>m<sup>2</sup></span>
              </p>
              <span :class="$style.link">
                {{ sliderComponent.linkText }}
              </span>
            </div>
          </div>
        </HgCardElevated>
      </template>
    </HgSlider>
  </SectionWithAnchor>
</template>

<script setup lang="ts">
import SectionWithAnchor from '@/components/common/SectionWithAnchor/SectionWithAnchor.vue';
import { getLocationInfos } from '@/helpers/GeoLocation/GeoLocationHelper';
import { formatLocalizedNumber } from '@/helpers/Number/FormatLocalizedNumber';
import { getCurrentPlatform } from '@/helpers/Platform/Platform';
import { scrollIntoViewByElement } from '@/helpers/Route/routeHelper';
import { getLocalLandingPagePath } from '@/helpers/Url/Url';
import { useCommonStore } from '@/stores/common/common';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { trackingGenericClickEvent } from '@/tracking/tracking';
import {
  LOCAL_LANDING_PAGE_ACTION,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
} from '@/tracking/types';
import { AnchorLinks, BREAKPOINTS, PropertyType, geoIdToRouteLocationMapping } from '@/types';
import {
  HgCardElevated,
  HgIcon,
  HgSlider,
  HgSpinner,
  SupportedPlatforms,
} from '@smg-real-estate/domus';
import { GeoLocation } from '@smg-real-estate/geo-libs';
import { computed, onBeforeMount, onMounted, onServerPrefetch, ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  includeCurrentLocation?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  includeCurrentLocation: true,
});

const commonStore = useCommonStore();
const localLandingStore = useLocalLandingStore();
const { t, locale } = useI18n();

// Reactive data
const platform = getCurrentPlatform();
const images = ref({});

const sliderBreakpoints = {
  [BREAKPOINTS.domus_lg - 0.1]: {
    perView: 3,
    peek: { before: 0, after: 0 },
  },
  [BREAKPOINTS.domus_md - 0.1]: {
    perView: 1,
    peek: { before: 0, after: 40 },
  },
};

const isResolvingLocations = computed(() => commonStore.isResolvingLocations);
const routeGeoLocations = computed(() => commonStore.routeGeoLocations);
const routeLocationsPrice = computed(() => commonStore.routeLocationsPrice);

// Computed properties
const geoLocations = computed(() => {
  const currentRouteGeoLocationId = localLandingStore.routeGeoLocation?.id ?? null;
  if (props.includeCurrentLocation || !currentRouteGeoLocationId) {
    return routeGeoLocations.value;
  }
  return routeGeoLocations.value.filter(
    (location: GeoLocation) => location.id !== currentRouteGeoLocationId,
  );
});

const locationsPrice = computed(() => {
  const currentRouteGeoLocationId = localLandingStore.routeGeoLocation?.id ?? null;
  if (props.includeCurrentLocation || !currentRouteGeoLocationId) {
    return routeLocationsPrice.value;
  }
  const targetIndex = routeGeoLocations.value.findIndex(
    (location: GeoLocation) => location.id === currentRouteGeoLocationId,
  );
  return routeLocationsPrice.value.filter((_, index) => index !== targetIndex);
});

// Methods
const resolveRouteGeoLocations = () => commonStore.resolveRouteGeoLocations();
const resolveLocationByGeoLocation = (geoLocation: GeoLocation) =>
  localLandingStore.resolveLocationByGeoLocation(geoLocation);

const getPrice = (index: number): { priceForHouse: string; priceForApartment: string } => {
  const rounded = platform === SupportedPlatforms.hg;
  const targetData = locationsPrice.value[index];
  if (!targetData) {
    return {
      priceForHouse: '',
      priceForApartment: '',
    };
  }

  return {
    priceForHouse: formatLocalizedNumber(
      targetData?.results?.[PropertyType.House]?.[0]?.priceValueMedian,
      rounded,
      locale.value as any,
    ),
    priceForApartment: formatLocalizedNumber(
      targetData?.results?.[PropertyType.Apartment]?.[0]?.priceValueMedian,
      rounded,
      locale.value as any,
    ),
  };
};

const locationsData = computed(() => {
  return geoLocations.value.map((location: GeoLocation, index: number) => {
    const locationInfos = getLocationInfos(location, locale.value as any);
    const href = getLocalLandingPagePath(locale.value as any, locationInfos.urlName);
    return {
      title: locationInfos.name,
      text: getPrice(index),
      href,
      linkText: t('button.learnMore'),
      geoLocation: location,
    };
  });
});

const handleTrackingEvent = (geoId: string) => {
  trackingGenericClickEvent({
    event: LOCAL_LANDING_PAGE_EVENT.BUTTON_CLICK,
    category: LOCAL_LANDING_PAGE_CATEGORY.BUTTON_CLICK,
    action: LOCAL_LANDING_PAGE_ACTION.OPEN_LLP,
    label: geoId,
  });
};

const handleLinkClick = async (geoLocation: GeoLocation) => {
  if (localLandingStore.isResolvingLocation) return;
  handleTrackingEvent(geoLocation.id);
  scrollIntoViewByElement();
  await resolveLocationByGeoLocation(geoLocation);
};

// Lifecycle hooks
onBeforeMount(async () => {
  const imagesArray = await Promise.all(
    routeGeoLocations.value.map(async (location: GeoLocation) => {
      try {
        const image = await import(
          `@/assets/images/local-landing-location-carousel/${platform}/${geoIdToRouteLocationMapping[location.id]}.webp`
        );
        return { [location.id]: image.default };
      } catch {
        return { [location.id]: '' };
      }
    }),
  );

  images.value = Object.assign({}, ...imagesArray);
});

onMounted(async () => {
  if (!routeGeoLocations.value.length) {
    await resolveRouteGeoLocations();
  }
});

onServerPrefetch(async () => {
  await resolveRouteGeoLocations();
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

$image-height: 170px;
$spinner-height: 338px;
$icon-size: $space-150;

.container {
  li {
    height: 100%;
  }
}

.heading {
  @include vertical-margin(0, $space-100);
  @include heading-2;
}

.spinner {
  width: 100%;
  height: $spinner-height;
  padding-top: $space-200;
}

.cardWrapper {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 3px;

  > a[class*='HgCardElevated_link'] {
    &:hover,
    &:focus {
      box-shadow: $elevation-minimal;
    }

    &:focus::after {
      box-shadow: unset;
    }
  }

  .image {
    width: 100%;
    height: $image-height;
    object-fit: cover;
  }
}

.contentWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-50;
  padding: $space-100;
}

.cardTitle {
  @include vertical-margin;
  @include heading-4($color-black);
}

.cardPrice {
  display: flex;
  align-items: baseline;

  @include vertical-margin;
  @include body-1($color-grey-100);

  > span {
    line-height: 1.2;
  }

  > svg {
    align-self: center;
    margin-right: $space-50;
  }
}

.icon {
  @include color-icon($color-grey-100);
}

.link {
  margin-top: $space-50;
  color: $color-azul-110;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
}
</style>
